import React from 'react';
import { Folder, Calendar, Building, Users, TrendingUp } from 'lucide-react';
import { Proyecto } from '../types';

interface ProyectoCardProps {
  proyecto: Proyecto;
  layoutMode: 'grid' | 'list';
  onClick?: () => void;
}

const ProyectoCard: React.FC<ProyectoCardProps> = ({ proyecto, layoutMode, onClick }) => {
  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'Planificación':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800';
      case 'En Progreso':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800';
      case 'Completado':
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800';
      default:
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-800';
    }
  };

  const formatFecha = (fecha: string) => {
    return new Date(fecha).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (layoutMode === 'list') {
    return (
      <div
        onClick={onClick}
        className="card p-4 hover:shadow-md transition-all cursor-pointer"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1">
            <div
              className="w-12 h-12 rounded-md flex items-center justify-center flex-shrink-0"
              style={{ color: proyecto.area.color || '#9333EA' }}
            >
              <Folder size={24} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 mb-1">
                {proyecto.nombre}
              </h3>
              {proyecto.descripcion && (
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2 line-clamp-2">
                  {proyecto.descripcion}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-3 text-sm text-neutral-600 dark:text-neutral-400">
                <div className="flex items-center">
                  <div
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: proyecto.area.color }}
                  />
                  <span>{proyecto.area.nombre}</span>
                </div>
                {proyecto.clienteEmpresa && (
                  <div className="flex items-center">
                    <Building size={14} className="mr-1 text-neutral-400" />
                    {proyecto.clienteEmpresa}
                  </div>
                )}
                <div className="flex items-center">
                  <Calendar size={14} className="mr-1 text-neutral-400" />
                  {formatFecha(proyecto.fechaInicio)}
                  {proyecto.fechaFin && ` → ${formatFecha(proyecto.fechaFin)}`}
                </div>
                {proyecto.servicios.length > 0 && (
                  <div className="flex items-center">
                    <Users size={14} className="mr-1 text-neutral-400" />
                    {proyecto.servicios.length} servicio{proyecto.servicios.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>
          </div>
          <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getEstadoColor(proyecto.estado)}`}>
            {proyecto.estado}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className="card p-6 hover:shadow-md transition-all cursor-pointer"
    >
      <div className="flex items-start justify-between mb-4">
        <div
          className="w-12 h-12 rounded-md flex items-center justify-center"
          style={{ color: proyecto.area.color || '#9333EA' }}
        >
          <Folder size={24} />
        </div>
        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getEstadoColor(proyecto.estado)}`}>
          {proyecto.estado}
        </span>
      </div>

      <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 mb-2">
        {proyecto.nombre}
      </h3>

      {proyecto.descripcion && (
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4 line-clamp-2">
          {proyecto.descripcion}
        </p>
      )}

      <div className="space-y-2">
        <div className="flex items-center text-sm text-neutral-600 dark:text-neutral-400">
          <div
            className="w-3 h-3 rounded-full mr-2"
            style={{ backgroundColor: proyecto.area.color }}
          />
          <span className="font-medium">{proyecto.area.nombre}</span>
        </div>

        {proyecto.clienteEmpresa && (
          <div className="flex items-center text-sm text-neutral-600 dark:text-neutral-400">
            <Building size={14} className="mr-2 text-neutral-400" />
            {proyecto.clienteEmpresa}
          </div>
        )}

        <div className="flex items-center text-sm text-neutral-600 dark:text-neutral-400">
          <Calendar size={14} className="mr-2 text-neutral-400" />
          <div>
            <div>{formatFecha(proyecto.fechaInicio)}</div>
            {proyecto.fechaFin && (
              <div className="text-xs text-neutral-500">→ {formatFecha(proyecto.fechaFin)}</div>
            )}
          </div>
        </div>

        {proyecto.servicios.length > 0 && (
          <div className="pt-2 border-t border-neutral-200 dark:border-neutral-700">
            <div className="flex items-center text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              <TrendingUp size={12} className="mr-1 text-neutral-400" />
              Servicios ({proyecto.servicios.length})
            </div>
            <div className="flex flex-wrap gap-1">
              {proyecto.servicios.slice(0, 3).map(ps => (
                <span
                  key={ps.id}
                  className="px-2 py-0.5 bg-neutral-100 dark:bg-neutral-800 rounded text-xs text-neutral-700 dark:text-neutral-300"
                >
                  {ps.servicio.tecnologia}
                </span>
              ))}
              {proyecto.servicios.length > 3 && (
                <span className="px-2 py-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                  +{proyecto.servicios.length - 3} más
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProyectoCard;
